import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMessage, setAiLoading } from '../store/chatSlice';
import { filterSensitive } from '../utils/sensitiveWords';

/**
 * 右侧聊天栏
 * - 观众消息列表
 * - AI 助手输入框
 * - 消息发送
 */
function ChatSidebar({ token, roomId, user }) {
  const dispatch = useDispatch();
  const { messages, aiLoading, viewerCount } = useSelector((state) => state.chat);
  const [input, setInput] = useState('');
  const [aiInput, setAiInput] = useState('');
  const messagesEndRef = useRef(null);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 发送聊天消息（本地显示，无需后端）
  const handleSend = () => {
    const content = filterSensitive(input.trim());
    if (!content) return;

    dispatch(addMessage({ content, type: 'chat', sender: user?.username || '游客', timestamp: new Date().toISOString() }));

    setInput('');
  };

  // AI 助手查询
  const handleAiQuery = async () => {
    const question = aiInput.trim();
    if (!question || aiLoading) return;

    dispatch(addMessage({ content: question, type: 'chat', sender: '我', timestamp: new Date().toISOString() }));
    dispatch(setAiLoading(true));

    try {
      const res = await fetch('/api/ai-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, roomId }),
      });
      const data = await res.json();
      dispatch(addMessage({
        content: data.answer || data.error || 'AI 暂时无法回答',
        type: 'ai',
        sender: 'AI助手',
        timestamp: new Date().toISOString(),
      }));
    } catch (err) {
      dispatch(addMessage({ content: 'AI 查询失败，请稍后重试', type: 'ai', sender: 'AI助手', timestamp: new Date().toISOString() }));
    }

    dispatch(setAiLoading(false));
    setAiInput('');
  };

  return (
    <div className="flex flex-col h-full bg-dark-800 rounded-lg">
      {/* 头部 */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <h3 className="font-semibold">💬 聊天</h3>
        <span className="text-sm text-gray-400">👁 {viewerCount} 人观看</span>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: '60vh' }}>
        {messages.map((msg, i) => (
          <div key={i} className={`text-sm ${msg.type === 'ai' ? 'bg-blue-900/30 p-2 rounded' : ''}`}>
            <span className={`font-semibold ${msg.type === 'ai' ? 'text-blue-400' : 'text-green-400'}`}>
              {msg.sender || '匿名'}:
            </span>
            <span className="ml-2 text-gray-200">{msg.content}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* AI 助手 */}
      <div className="p-3 border-t border-gray-700">
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="🤖 问 AI 助手..."
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAiQuery()}
            className="flex-1 p-2 bg-dark-700 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
            disabled={aiLoading}
          />
          <button
            onClick={handleAiQuery}
            disabled={aiLoading}
            className="px-3 bg-blue-600 hover:bg-blue-700 rounded text-sm font-semibold disabled:opacity-50 transition"
          >
            {aiLoading ? '⏳' : '🤖'}
          </button>
        </div>
      </div>

      {/* 消息输入 */}
      <div className="p-3 border-t border-gray-700">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="发送消息..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            className="flex-1 p-2 bg-dark-700 rounded text-sm border border-gray-600 focus:border-green-500 focus:outline-none"
          />
          <button
            onClick={handleSend}
            className="px-4 bg-green-600 hover:bg-green-700 rounded text-sm font-semibold transition"
          >
            发送
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatSidebar;
